import { Drawer } from 'antd';
import classNames from 'classnames';
import styles from './ViewDetailsDrawer.module.scss';
import { Icon } from 'Components';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';
import { certificationLabel } from 'LearningManager/helpers/assignment';
import { formatCardDate } from 'LearningManager/helpers/date';

interface ViewDetailsDrawerProps {
	visible: boolean;
	assignment: Assignment | null;
	source?: AssignmentSource;
	onClose: () => void;
}

interface ContentItem {
	key: string;
	label: string;
}

const NOT_AVAILABLE = '-';

const SCOPE_LABEL: Record<AssignmentSource['scopes'][number], string> = {
	group_labels: 'Organization',
	groups: 'Groups',
	roles: 'Roles',
	users: 'Individual users'
};

const contentItems = (assignment: Assignment | null): ContentItem[] => {
	const learningPaths = assignment?.learningPaths ?? [];
	const learningPathCourseIds = new Set(
		learningPaths.flatMap((lp) => lp.courses.map((course) => course.id))
	);
	return [
		...learningPaths.map((lp) => ({ key: lp.id, label: lp.label })),
		...(assignment?.individualCourses ?? [])
			.filter((course) => !learningPathCourseIds.has(course.id))
			.map((course) => ({
				key: `course-${course.id}`,
				label: course.title
			}))
	];
};

const ViewDetailsDrawer: React.FC<ViewDetailsDrawerProps> = ({
	visible,
	assignment,
	source,
	onClose
}) => {
	const content = contentItems(assignment);
	const scopes = source?.scopes ?? [];
	const completionPercentage = assignment?.completionPercentage ?? 0;

	return (
		<Drawer
			className={styles.assignmentDetailsDrawer}
			placement="right"
			visible={visible}
			width={420}
			onClose={onClose}
			title={
				<div className={styles.drawerTitle}>
					<div className={styles.drawerTitle__name}>
						{assignment?.name}
					</div>
					<div className={styles.drawerTitle__badges}>
						{assignment?.type && (
							<AssignmentBadge type={assignment.type} />
						)}
						{assignment?.status && (
							<AssignmentBadge type={assignment.status} />
						)}
					</div>
				</div>
			}
		>
			<div className={styles.drawerSection}>
				<div className={styles.drawerSection__heading}>Details</div>
				<div className={styles.detailLine}>
					Assignment #: {assignment?.id ?? NOT_AVAILABLE}
				</div>
				<div className={styles.detailLine}>
					Created: {NOT_AVAILABLE}
				</div>
				<div className={styles.detailLine}>
					Updated: {NOT_AVAILABLE}
				</div>
				<div className={styles.detailLine}>
					Start:{' '}
					{formatCardDate(assignment?.startsDate) || NOT_AVAILABLE}
				</div>
				<div className={styles.detailLine}>
					Duration: {NOT_AVAILABLE}
				</div>
			</div>

			<div className={styles.drawerSection}>
				<div className={styles.progressHeader}>
					<div className={styles.drawerSection__heading}>
						Progress Overview
					</div>
					<Icon type="infoOutlined" role="img" />
				</div>
				<div className={styles.progressTrack}>
					<div
						className={classNames(
							styles.progressFill,
							styles['progressFill--completed']
						)}
						style={{ width: `${completionPercentage}%` }}
					/>
				</div>
				<div className={styles.progressLegendContainer}>
					<div className={styles.progresslegend}>
						<span
							className={classNames(
								styles.progressDot,
								styles['progressDot--completed']
							)}
						/>
						Completed: {assignment?.completedCount ?? NOT_AVAILABLE}
					</div>
					<div className={styles.progresslegend}>
						<span
							className={classNames(
								styles.progressDot,
								styles['progressDot--inProgress']
							)}
						/>
						In Progress: {NOT_AVAILABLE}
					</div>
					<div className={styles.progresslegend}>
						<span className={styles.progressDot} />
						Not Started: {NOT_AVAILABLE}
					</div>
				</div>
			</div>

			<div className={styles.drawerSection}>
				<div className={styles.drawerSection__heading}>Content</div>
				{content.length ? (
					content.map((item, index) => (
						<div key={item.key} className={styles.listLine}>
							{index + 1}. {item.label}
						</div>
					))
				) : (
					<div className={styles.listLine}>{NOT_AVAILABLE}</div>
				)}
			</div>

			<div className={styles.drawerSection}>
				<div className={styles.drawerSection__heading}>Assignment</div>
				{scopes.length ? (
					scopes.map((scope) => (
						<div key={scope} className={styles.detailLine}>
							{SCOPE_LABEL[scope] ?? scope}
						</div>
					))
				) : (
					<div className={styles.detailLine}>{NOT_AVAILABLE}</div>
				)}
			</div>

			<div className={styles.drawerSection}>
				<div className={styles.drawerSection__heading}>Scheduling</div>
				<div className={styles.detailLine}>
					Due:{' '}
					{assignment?.dueDate
						? formatCardDate(assignment.dueDate)
						: 'None'}
				</div>
				<div className={styles.detailLine}>
					Certification expiration:{' '}
					{certificationLabel(source) || NOT_AVAILABLE}
				</div>
			</div>

			<div className={styles.drawerSection}>
				<div className={styles.drawerSection__heading}>Languages</div>
				<div className={styles.detailLine}>{NOT_AVAILABLE}</div>
			</div>
		</Drawer>
	);
};

export default ViewDetailsDrawer;
